<template>
  <div class="tool-intro-management-container">
    <div class="page-header">
      <h2>工具介绍管理</h2>
      <el-button type="primary" @click="showAddDialog">
        <el-icon><Plus /></el-icon> 添加工具介绍
      </el-button>
    </div>
    
    <!-- 工具介绍列表 -->
    <el-table
      v-loading="loading"
      :data="introductions"
      border
      style="width: 100%; margin-top: 20px;"
    >
      <el-table-column type="index" width="50" align="center" />
      
      <el-table-column label="封面" width="100">
        <template #default="scope">
          <el-image
            style="width: 60px; height: 40px;"
            :src="scope.row.cover_image || '/default-tool-cover.png'"
            fit="cover"
            :preview-src-list="scope.row.cover_image ? [scope.row.cover_image] : []"
            :initial-index="0"
          >
            <template #error>
              <div class="image-error">
                <el-icon><Picture /></el-icon>
              </div>
            </template>
          </el-image>
        </template>
      </el-table-column>
      
      <el-table-column prop="tool_key" label="工具标识" width="150" />
      
      <el-table-column prop="title" label="标题" min-width="150" />
      
      <el-table-column prop="description" label="简介" min-width="200">
        <template #default="scope">
          <el-tooltip
            v-if="scope.row.description && scope.row.description.length > 50"
            class="box-item"
            effect="dark"
            :content="scope.row.description"
            placement="top-start"
          >
            <span>{{ scope.row.description.substring(0, 50) }}...</span>
          </el-tooltip>
          <span v-else>{{ scope.row.description || '-' }}</span>
        </template>
      </el-table-column>
      
      <el-table-column label="更新时间" width="180">
        <template #default="scope">
          {{ formatDateTime(scope.row.updated_at) }}
        </template>
      </el-table-column>
      
      <el-table-column
        label="操作"
        width="180"
        fixed="right"
        align="center"
      >
        <template #default="scope">
          <el-button
            type="primary"
            size="small"
            @click="handleEdit(scope.row)"
          >
            编辑
          </el-button>
          <el-button
            type="danger"
            size="small"
            @click="handleDelete(scope.row)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    
    <!-- 表单对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="isEdit ? '编辑工具介绍' : '添加工具介绍'"
      width="80%"
      :before-close="handleDialogClose"
    >
      <el-form
        ref="formRef"
        :model="form"
        :rules="rules"
        label-width="100px"
      >
        <el-form-item label="工具标识" prop="tool_key">
          <el-input 
            v-model="form.tool_key" 
            placeholder="请输入工具标识，例如：jianbiqiji-ai"
            :disabled="isEdit"
          />
          <div class="form-tip">唯一标识符，用于API调用，建议使用英文、数字和连字符</div>
        </el-form-item>
        
        <el-form-item label="标题" prop="title">
          <el-input v-model="form.title" placeholder="请输入工具标题" />
        </el-form-item>
        
        <el-form-item label="简介" prop="description">
          <el-input
            v-model="form.description"
            type="textarea"
            :rows="3"
            placeholder="请输入工具简介"
          />
        </el-form-item>
        
        <el-form-item label="封面图片">
          <div class="upload-container">
            <el-upload
              class="avatar-uploader"
              :http-request="handleCoverUpload"
              :show-file-list="false"
              :before-upload="beforeCoverUpload"
              accept=".jpg,.jpeg,.png,.gif,.webp"
            >
              <img 
                v-if="form.cover_image" 
                :src="form.cover_image" 
                class="avatar" 
              />
              <el-icon v-else class="avatar-uploader-icon"><Plus /></el-icon>
            </el-upload>
            <div class="upload-tips">
              <p>支持 jpg, png, webp 格式，建议使用高质量图片</p>
            </div>
          </div>
        </el-form-item>
        
        <el-form-item label="详细内容" prop="content">
          <div class="editor-container">
            <!-- 使用富文本编辑器 -->
            <RichTextEditor
              v-model="form.content"
              height="400px"
              placeholder="请输入工具介绍内容，支持图片、视频和文字"
            />
          </div>
          <div class="form-tip">支持添加图片、视频、表格等富文本内容</div>
        </el-form-item>
        
        <el-form-item>
          <el-button type="primary" @click="submitForm" :loading="submitting">
            {{ isEdit ? '保存修改' : '添加工具介绍' }}
          </el-button>
          <el-button @click="handleDialogClose">取消</el-button>
        </el-form-item>
      </el-form>
    </el-dialog>
    
    <!-- 删除确认对话框 -->
    <el-dialog
      v-model="deleteDialogVisible"
      title="确认删除"
      width="30%"
      center
    >
      <p>确定要删除工具介绍 "{{ selectedItem?.title }}" 吗？此操作无法恢复。</p>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="deleteDialogVisible = false">取消</el-button>
          <el-button 
            type="danger" 
            @click="confirmDelete" 
            :loading="deleting"
          >
            确认删除
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Plus, Picture } from '@element-plus/icons-vue';
import { 
  fetchToolIntroductions, 
  uploadToolIntroImage, 
  saveToolIntroduction, 
  deleteToolIntroduction,
  fetchToolIntroduction
} from '@/api/toolIntroduction';
import RichTextEditor from '@/components/editor/RichTextEditor.vue';

// 工具介绍列表
const introductions = ref([]);
const loading = ref(true);

// 表单相关
const formRef = ref(null);
const dialogVisible = ref(false);
const isEdit = ref(false);
const submitting = ref(false);
const form = reactive({
  id: null,
  tool_key: '',
  title: '',
  description: '',
  cover_image: '',
  content: ''
});

// 格式化日期时间
const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// 表单验证规则
const rules = {
  tool_key: [
    { required: true, message: '请输入工具标识', trigger: 'blur' },
    { pattern: /^[a-zA-Z0-9-]+$/, message: '工具标识只能包含英文、数字和连字符', trigger: 'blur' },
    { max: 100, message: '标识长度不能超过100个字符', trigger: 'blur' }
  ],
  title: [
    { required: true, message: '请输入工具标题', trigger: 'blur' },
    { max: 255, message: '标题长度不能超过255个字符', trigger: 'blur' }
  ],
  description: [
    { max: 500, message: '简介长度不能超过500个字符', trigger: 'blur' }
  ]
};

// 删除相关
const deleteDialogVisible = ref(false);
const deleting = ref(false);
const selectedItem = ref(null);

// 获取工具介绍列表
const fetchIntroductions = async () => {
  loading.value = true;
  
  try {
    const data = await fetchToolIntroductions();
    introductions.value = data;
  } catch (err) {
    console.error('获取工具介绍列表失败:', err);
    ElMessage.error('获取工具介绍列表失败，请稍后再试');
  } finally {
    loading.value = false;
  }
};

// 显示添加对话框
const showAddDialog = () => {
  isEdit.value = false;
  resetForm();
  dialogVisible.value = true;
};

// 处理编辑
const handleEdit = (item) => {
  isEdit.value = true;
  
  // 复制对象，避免直接修改原始数据
  form.id = item.id;
  form.tool_key = item.tool_key;
  form.title = item.title;
  form.description = item.description || '';
  form.cover_image = item.cover_image || '';
  
  // 处理content字段可能为null的情况
  form.content = item.content || '';
  
  // 如果content字段不存在或为空，尝试从服务器获取完整的工具介绍信息
  if (!item.content) {
    fetchSingleToolIntroduction(item.tool_key);
  }
  
  dialogVisible.value = true;
};

// 获取单个完整的工具介绍数据
const fetchSingleToolIntroduction = async (toolKey) => {
  try {
    const data = await fetchToolIntroduction(toolKey);
    if (data && data.content) {
      form.content = data.content;
    }
  } catch (err) {
    console.error('获取工具介绍详情失败:', err);
    ElMessage.warning('获取工具介绍详情内容失败，可能需要重新编辑内容');
  }
};

// 处理删除
const handleDelete = (item) => {
  selectedItem.value = item;
  deleteDialogVisible.value = true;
};

// 确认删除
const confirmDelete = async () => {
  if (!selectedItem.value) return;
  
  deleting.value = true;
  
  try {
    await deleteToolIntroduction(selectedItem.value.id);
    ElMessage.success('工具介绍删除成功');
    fetchIntroductions();
  } catch (err) {
    console.error('删除工具介绍失败:', err);
    ElMessage.error('删除工具介绍失败，请稍后再试');
  } finally {
    deleting.value = false;
    deleteDialogVisible.value = false;
    selectedItem.value = null;
  }
};

// 封面图片上传前验证
const beforeCoverUpload = (file) => {
  const isImage = /image\/(jpeg|png|gif|jpg|webp)/.test(file.type);
  const isLt5M = file.size / 1024 / 1024 < 5;
  
  if (!isImage) {
    ElMessage.error('请上传图片文件!');
    return false;
  }
  
  if (!isLt5M) {
    ElMessage.error('图片大小不能超过5MB!');
    return false;
  }
  
  return true;
};

// 处理封面图片上传
const handleCoverUpload = async ({ file }) => {
  try {
    const response = await uploadToolIntroImage(file);
    
    if (response && response.url) {
      form.cover_image = response.url;
      ElMessage.success('封面图片上传成功');
    } else {
      ElMessage.error('封面图片上传失败');
    }
  } catch (err) {
    console.error('封面图片上传失败:', err);
    ElMessage.error('封面图片上传失败，请稍后再试');
  }
};

// 提交表单
const submitForm = async () => {
  if (!formRef.value) return;
  
  formRef.value.validate(async (valid) => {
    if (!valid) {
      return false;
    }
    
    submitting.value = true;
    
    try {
      const formData = {
        tool_key: form.tool_key,
        title: form.title,
        description: form.description,
        content: form.content,
        cover_image: form.cover_image
      };
      
      // 如果是编辑模式，添加id字段
      if (isEdit.value && form.id) {
        formData.id = form.id;
      }
      
      await saveToolIntroduction(formData);
      
      ElMessage.success(isEdit.value ? '工具介绍更新成功' : '工具介绍添加成功');
      dialogVisible.value = false;
      fetchIntroductions();
    } catch (err) {
      console.error('提交工具介绍失败:', err);
      ElMessage.error('提交工具介绍失败，请稍后再试');
    } finally {
      submitting.value = false;
    }
  });
};

// 重置表单
const resetForm = () => {
  form.id = null;
  form.tool_key = '';
  form.title = '';
  form.description = '';
  form.cover_image = '';
  form.content = '';
  
  // 如果表单引用存在，重置验证
  if (formRef.value) {
    formRef.value.resetFields();
  }
};

// 处理对话框关闭
const handleDialogClose = () => {
  ElMessageBox.confirm(
    '确认关闭？未保存的更改将丢失',
    '警告',
    {
      confirmButtonText: '确认',
      cancelButtonText: '取消',
      type: 'warning'
    }
  )
    .then(() => {
      dialogVisible.value = false;
      resetForm();
    })
    .catch(() => {
      // 用户取消关闭
    });
};

onMounted(() => {
  fetchIntroductions();
});
</script>

<style scoped>
.tool-intro-management-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  position: relative;
}

.page-header h2 {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  margin: 0;
  text-align: center;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
  margin-top: 4px;
}

.upload-container {
  display: flex;
  align-items: center;
}

.avatar-uploader {
  width: 160px;
}

.avatar-uploader:hover {
  border-color: var(--el-color-primary);
}

.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 178px;
  height: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.avatar {
  width: 178px;
  height: 100px;
  display: block;
  object-fit: cover;
}

.upload-tips {
  margin-left: 16px;
  color: #909399;
  font-size: 12px;
}

.editor-container {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
}

.image-error {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  color: #909399;
  font-size: 20px;
}
</style> 